import {FULFILLED} from 'redux-promise-middleware';

import {PRODUCT_TYPES} from 'constants/product';
import {PRODUCT} from 'constants/actions';
import {
    CATEGORY_STORE,
    RECENTLY_VIEWED_STORE,
    HP_FEATURED_STORE,
    HP_SHOP_THE_LOOK_STORE,
} from 'constants/category';
import {includes} from 'ramda';

import {take, call, select, all} from 'redux-saga/effects';
import {
    getParentProduct,
    getProductReviews,
    getIsProductFulfilled,
    getIsReviewsItemsFulfilled,
    getActiveChildrenProductId,
} from 'selectors/product/product';
import {getColorsSizesProduct} from 'selectors/product/productColors';
import {getUpsellWithoutActiveProduct, isUpsellItemsFulfilled} from 'selectors/upsell/upsell';
import {getProperty, waitForFulfilled} from 'libs/utils/utils';
import {localStore, sessionStore} from 'libs/storage/storage';

/**
 * Get recently viewed product for EEC impressions.
 *
 * @returns {SagaIterator} - Recently viewed products.
 */
export function* getRecentlyViewedProducts() {
    const products = yield select(getUpsellWithoutActiveProduct);

    return (products || []).map(product => ({...product, list: 'Recently viewed in product'}));
}

/**
 * Get color, sizes product.
 * @param {string} type - Product type.
 *
 * @returns {SagaIterator} - Product colors, sizes.
 */
export function* getProductColorsSizes(type: string) {
    return includes(type, [
        PRODUCT_TYPES.EYEGLASSES,
        PRODUCT_TYPES.SUNGLASSES,
        PRODUCT_TYPES.READERS,
    ])
        ? yield select(getColorsSizesProduct)
        : [];
}

/**
 * Wait for recently viewed.
 */
export function* waitForRecentlyViewed() {
    const recentlyViewed = yield call([localStore, 'get'], RECENTLY_VIEWED_STORE);

    if (recentlyViewed)
        yield call(waitForFulfilled, isUpsellItemsFulfilled, `${PRODUCT.GET_UPSELL}_${FULFILLED}`);
}

/**
 * Function to get active product id which was set by force
 * @returns {number} active product id
 */
export function* forceGetActiveProductId() {
    const {payload: productId} = yield take(PRODUCT.SET_ACTIVE_PRODUCT_ID);
    return productId;
}

/**
 * Get window and simple products
 * @returns {SagaIterator} - Window and simple products.
 */
export function* getProducts() {
    const [windowProduct, activeProductId] = yield all([
        select(getParentProduct),
        select(getActiveChildrenProductId),
    ]);
    const productId = activeProductId || (yield call(forceGetActiveProductId));

    const simpleProduct =
        getProperty(`children.${productId}`)(windowProduct) ||
        windowProduct.children.find(item => item.id === productId);

    if (windowProduct.type === PRODUCT_TYPES.CONTACT_LENSES)
        yield call(
            waitForFulfilled,
            getIsReviewsItemsFulfilled,
            `${PRODUCT.FETCH_REVIEWS}_${FULFILLED}`,
        );

    return [windowProduct, simpleProduct];
}

/**
 * Wait for PDP loaded.
 *
 * @returns {SagaIterator} - PDP data. windowProduct, productReviews, productColorsSizes, recentlyViewedProduct, isConfigurePage.
 */
export function* waitForPDP() {
    yield call(
        waitForFulfilled,
        getIsReviewsItemsFulfilled,
        `${PRODUCT.FETCH_REVIEWS}_${FULFILLED}`,
    );
    yield call(waitForFulfilled, getIsProductFulfilled, `${PRODUCT.GET_PRODUCT}_${FULFILLED}`);

    const [windowProduct, simpleProduct] = yield call(getProducts);

    const pdpData = yield all([
        select(getProductReviews),
        call(getProductColorsSizes, windowProduct.type),
    ]);

    return [simpleProduct, ...pdpData];
}

/**
 * Get category info from local storage.
 * @returns {SagaIterator} - Category info.
 */
export function* getProductCategory() {
    const categoryFromStore = yield call([sessionStore, 'get'], CATEGORY_STORE);
    const hpCategoryFromStore = yield call([sessionStore, 'get'], HP_FEATURED_STORE);
    const hpShopTheLookFromStore = yield call([sessionStore, 'get'], HP_SHOP_THE_LOOK_STORE);

    return {
        name: 'Non category page',
        path: '/',
        ...(categoryFromStore || hpCategoryFromStore || hpShopTheLookFromStore),
    };
}
